import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import { MAX_ENTITY_COUNT } from '../constants'
import { isClosedShape } from './entityPositions'
import { isIconShape } from './iconShapes'

const isAssignableShape = (shape: VisualShape) =>
  isClosedShape(shape) || isIconShape(shape)

const numberedEntityCount = (shape: VisualShape) => {
  if (!isAssignableShape(shape)) return 0
  return Math.min(Math.max(shape.entity_count ?? 0, 0), MAX_ENTITY_COUNT)
}

const hasEntityNumber = (shape: VisualShape) => numberedEntityCount(shape) > 0

export const getEntityNumberStarts = (
  shapes: VisualShape[],
): Map<string, number> => {
  const starts = new Map<string, number>()
  let next = 1
  for (const shape of [...shapes].reverse()) {
    const count = numberedEntityCount(shape)
    if (count <= 0) continue
    starts.set(shape.id, next)
    next += count
  }
  return starts
}

// Numbers run from the front of the stack backward, so the back-most numbered
// object has the last number. New assignments go just behind that object.
export const insertNewAssignmentsAfterLastNumbered = (
  previous: VisualShape[],
  next: VisualShape[],
): VisualShape[] => {
  const previousCount = new Map(
    previous.map((shape) => [shape.id, numberedEntityCount(shape)]),
  )
  const newlyAssigned = next.filter(
    (shape) =>
      numberedEntityCount(shape) > 0 && (previousCount.get(shape.id) ?? 0) <= 0,
  )
  if (newlyAssigned.length === 0) return next

  const moved = new Set(newlyAssigned.map((shape) => shape.id))
  const staying = next.filter((shape) => !moved.has(shape.id))
  const firstNumberedIndex = staying.findIndex(hasEntityNumber)
  const insertAt = firstNumberedIndex < 0 ? staying.length : firstNumberedIndex

  return [
    ...staying.slice(0, insertAt),
    ...newlyAssigned.reverse(),
    ...staying.slice(insertAt),
  ]
}

export const formatEntityNumberRange = (
  start: number,
  count: number,
): string => {
  if (count <= 1) return String(start)
  return `${start}-${start + count - 1}`
}
